import { Component, OnInit, Input, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DatatableComponent, DataTableColumnDirective } from '@swimlane/ngx-datatable';
import { NgModel } from '@angular/forms';
import * as moment_ from 'moment';
const moment = moment_;

export enum EnmFilterType {
    Equals = "equals",
    NotEquals = "notEquals",
    Contains = "contains",
    StartsWith = "startsWith",
    EndsWith = "endsWith",
    LowerThan = "lowerThan",
    LowerOrEqual = "lowerOrEqual",
    GreaterThan = "greaterThan",
    GreaterOrEqual = "greaterOrEqual",
    Empty = "empty",
    NotEmpty = "notEmpty",
    Between = "between"
}

export enum EnmPropertyType {
    Text = 1,
    Date = 2,
    Options = 3,
    DateTime = 4,
    DateTimeFull = 5,
    Time = 6,
    TimeFull = 7
}

@Component({
    selector: 'column-filter',
    templateUrl: './column-filter.component.html',
    styleUrls: ['./column-filter.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ColumnFilterComponent implements OnInit {
    @Input() label: string;
    @Input() hidden = false;
    @Input() sortDir: string;
    @Input() public sort: any;

    private _dataTable: DatatableComponent;
    get dataTable(): DatatableComponent {
        return this._dataTable;
    }
    @Input() set dataTable(dataTable: DatatableComponent) {
        this._dataTable = dataTable;
        ColumnFilterComponent.filterDatatable(dataTable);
    }
    @Input() prop: string;
    @Input() type: EnmPropertyType = EnmPropertyType.Text;

    private _options: { id: any, value: string }[];
    get options(): { id: any, value: string }[] {
        return this._options;
    }
    @Input() set options(o: { id: any, value: string }[]) {
        this._options = o;
        this._applyOptionsFilter();
    }

    private _initFilterValue: any;
    get initFilterValue(): any { return this._initFilterValue; }
    @Input() set initFilterValue(v: any) {
        this._initFilterValue = v;
        this._applyOptionsFilter();
    };

    @Input() initFilterType: EnmFilterType;

    @Input() serverSide = false;
    @Output() filterQuery = new EventEmitter();

    sortable: boolean;
    keys(): Array<string> {
        let values: any[] = Object.values(this.EnmFilterType);
        if (this.isDateOrTime(this.type)) {
            values = values.filter(f => f != EnmFilterType.StartsWith
                && f != EnmFilterType.EndsWith
                && f != EnmFilterType.Contains);
        }
        return values;
    }

    private _value: any = '';
    get value(): any {
        return this._value;
    }
    set value(value: any) {
        if (typeof value === 'string') this._value = (value as string || '').toLowerCase();
        else this._value = value;
    }

    private _valueMax: any = '';
    get valueMax(): any {
        return this._valueMax;
    }
    set valueMax(value: any) {
        if (typeof value === 'string') this._valueMax = (value as string || '').toLowerCase();
        else this._valueMax = value;
    }

    EnmFilterType: typeof EnmFilterType = EnmFilterType;
    EnmPropertyType: typeof EnmPropertyType = EnmPropertyType;

    private _filterType: EnmFilterType;
    get filterType(): EnmFilterType {
        return this._filterType;
    }
    set filterType(filterType: EnmFilterType) {
        this._symbol = this.getSymbol(filterType);
        this._explanation = this.getExplanation(filterType);
        this._filterType = filterType;
        if (filterType === EnmFilterType.Empty || filterType === EnmFilterType.NotEmpty) this.value = '';
    }

    private _symbol: string;
    get symbol(): string {
        return this._symbol;
    }

    private _explanation: string;
    get explanation(): string {
        return this._explanation;
    }

    private _selectedOptions: { id: any, value: string }[] = [];
    get selectedOptions(): { id: any, value: string }[] {
        return this._selectedOptions;
    }
    set selectedOptions(selectedOptions: { id: any, value: string }[]) {
        if (this._selectedOptions === selectedOptions) return;
        this._selectedOptions = selectedOptions.filter(o => o);

        if (this.serverSide) this.emitServerSideChange();
        else ColumnFilterComponent.filterDatatable(this.dataTable);
    }

    selectorType: 'both' | 'calendar' | 'timer';
    showSeconds = false;

    constructor(private translate: TranslateService) { }

    ngOnInit() {
        this.sortable = !!this.sort;
        if (this.initFilterType) this.filterType = this.initFilterType;
        else this.filterType = this.type === EnmPropertyType.Text ? EnmFilterType.Contains : EnmFilterType.Equals;

        if (this.label) this.label = this.translate.instant(this.label);

        if (this.options) this.type = EnmPropertyType.Options;
        if (this.type === EnmPropertyType.Options) this._applyOptionsFilter();
        else if (this.initFilterValue) this.value = this.initFilterValue;

        if (this.type === EnmPropertyType.Date) this.selectorType = 'calendar';
        else if (this.type === EnmPropertyType.DateTime || this.type === EnmPropertyType.DateTimeFull) {
            this.selectorType = 'both';
            this.showSeconds = this.type === EnmPropertyType.DateTimeFull;
        }
        else if (this.type === EnmPropertyType.Time || this.type === EnmPropertyType.TimeFull) {
            this.selectorType = 'timer';
            this.showSeconds = this.type === EnmPropertyType.TimeFull;
        }

        if (this.initFilterValue) ColumnFilterComponent.filterDatatable(this.dataTable);
    }

    isDateOrTime(type: EnmPropertyType): boolean {
        if (type === EnmPropertyType.Date) return true;
        else if (type === EnmPropertyType.DateTime) return true;
        else if (type === EnmPropertyType.DateTimeFull) return true;
        else if (type === EnmPropertyType.Time) return true;
        else if (type === EnmPropertyType.TimeFull) return true;

        return false;
    }

    isFilter(filterType: EnmFilterType): boolean {
        return this.filterType === filterType;
    }

    filter(filterType: EnmFilterType) {
        this.filterType = filterType;
        if (this.serverSide) this.emitServerSideChange();
        else ColumnFilterComponent.filterDatatable(this.dataTable);
    }

    getSymbol(filterType: EnmFilterType): string {
        return this.translate.instant("filter.symbol." + filterType)
    }
    getExplanation(filterType: EnmFilterType): string {
        return this.translate.instant("filter.explanation." + filterType)
    }

    equals(objOne, objTwo) {
        if (typeof objOne !== 'undefined' && typeof objTwo !== 'undefined') {
            return objOne.id === objTwo.id;
        }
    }

    selectAll(select: NgModel, values) {
        select.update.emit(values);
    }

    deselectAll(select: NgModel) {
        select.update.emit([]);
    }

    private _lastEvent: number;
    updateFilter() {
        const date: number = Date.now();
        this._lastEvent = date;

        setTimeout(() => {
            if (this._lastEvent === date) {
                if (this.serverSide) this.emitServerSideChange();
                else ColumnFilterComponent.filterDatatable(this.dataTable);
            }
        }, 500);
    }

    private _applyOptionsFilter() {
        if (this.type === EnmPropertyType.Options) {
            if (!this.initFilterValue) this.selectedOptions = [...this.options];
            else {
                for (const option of this.initFilterValue) {
                    this.options.filter(o => o.id === option.id).forEach(o => this.selectedOptions.push(o));
                }
            }
        }
        ColumnFilterComponent.filterDatatable(this.dataTable);
    }

    static applyFilter(dataTable: DatatableComponent): void {
        (dataTable as any).originalRows = null;
        ColumnFilterComponent.filterDatatable(dataTable);
    }

    static resetFilter(dataTable: DatatableComponent): void {
        dataTable.columnTemplates
            .filter((c: any) => c.headerTemplate
                && c.headerTemplate._projectedViews
                && c.headerTemplate._projectedViews.length)
            .filter((v: any) => v.nodes
                && v.nodes.length
                && v.nodes.filter((n: any) => n.instance
                    && n.instance instanceof ColumnFilterComponent).length)
            .map((v: any) => v.nodes.filter((n: any) => n.instance && n.instance instanceof ColumnFilterComponent)
                .map((n: any) => n.instance))
            .forEach(r => r.forEach((i: ColumnFilterComponent) => {
                i.value = '';
                i.filterType = EnmFilterType.Contains;
            }));
        ColumnFilterComponent.filterDatatable(dataTable);
        //emitServerSideChange();
    }

    private emitServerSideChange() {
        const filterQuery: string = ColumnFilterComponent.getServerSideFilter(this.dataTable);
        this.filterQuery.emit(filterQuery);
    }

    static getServerSideFilter(dataTable: DatatableComponent): string {
        //get columns with a defined header
        let columns: DataTableColumnDirective[] = dataTable.columnTemplates
            .filter((c: any) => c.headerTemplate
                && c.headerTemplate._projectedViews
                && c.headerTemplate._projectedViews.length);

        const filterFragments: string[] = [];

        for (let c of columns) {
            let filters: ColumnFilterComponent[][] = (c.headerTemplate as any)._projectedViews
                .filter((v: any) => v.nodes
                    && v.nodes.length
                    && v.nodes.filter((n: any) => n.instance
                        && n.instance instanceof ColumnFilterComponent
                        && (n.instance as ColumnFilterComponent).serverSide).length)
                .map((v: any) => v.nodes.filter((n: any) => n.instance && n.instance instanceof ColumnFilterComponent && (n.instance as ColumnFilterComponent).serverSide)
                    .map((n: any) => n.instance)) as ColumnFilterComponent[][];
            if (!filters.length) continue;

            const filter: ColumnFilterComponent = filters[0][0];

            let sign: string = filter.filterType;

            let value: any = '';
            if (filter.type === EnmPropertyType.Text) value = filter.value;
            else if (filter.type === EnmPropertyType.Options) {
                value = filter.selectedOptions.map(o => o.id).join(',');
                sign = 'options';
            }
            else {
                let format: string = '';

                if (filter.type === EnmPropertyType.Date) format = "YYYYMMDD";
                else if (filter.type === EnmPropertyType.DateTime) format = "YYYYMMDDHHmm";
                else if (filter.type === EnmPropertyType.DateTimeFull) format = "YYYYMMDDHHmmss";
                else if (filter.type === EnmPropertyType.Time) format = "HHmm";
                else if (filter.type === EnmPropertyType.TimeFull) format = "HHmmss";

                if (filter.value) value = filter.value.format(format);
            }

            filterFragments.push('filter=' + btoa(filter.prop + ':::' + sign + ':::' + value));
        }

        const filterQuery: string = filterFragments.join('&');
        return filterQuery;
    }

    static filterDatatable(dataTable: DatatableComponent, rows?: {}[]): void {

        if (rows) (dataTable as any).originalRows = rows;

        //Store a copy of original rows the first time to restore data later
        if (!(dataTable as any).originalRows) {
            if (!dataTable || !dataTable.rows || !dataTable.rows.length) return;
            (dataTable as any).originalRows = dataTable.rows;
        }

        //get columns with a defined header
        let columns: DataTableColumnDirective[] = dataTable.columnTemplates
            .filter((c: any) => c.headerTemplate
                && c.headerTemplate._projectedViews
                && c.headerTemplate._projectedViews.length);

        //start the filter from the original rows
        let data: {}[] = (dataTable as any).originalRows;

        for (let c of columns) {
            //get filter from header (retrieve header from an hidden property)
            let filters: ColumnFilterComponent[][] = (c.headerTemplate as any)._projectedViews
                .filter((v: any) => v.nodes
                    && v.nodes.length
                    && v.nodes.filter((n: any) => n.instance
                        && n.instance instanceof ColumnFilterComponent
                        && !(n.instance as ColumnFilterComponent).serverSide).length)
                .map((v: any) => v.nodes.filter((n: any) => n.instance && n.instance instanceof ColumnFilterComponent && !(n.instance as ColumnFilterComponent).serverSide)
                    .map((n: any) => n.instance)) as ColumnFilterComponent[][];
            if (!filters.length) continue;

            const filter: ColumnFilterComponent = filters[0][0];

            if (filter.serverSide) continue;

            if (filter.type === EnmPropertyType.Text) data = this.filterText(filter, data);
            else if (filter.type === EnmPropertyType.Options) data = this.filterOptions(filter, data);
            else data = this.filterDate(filter, data);
        }

        // update the rows
        dataTable.rows = [...data];
        // Whenever the filter changes, always go back to the first page 
        //dataTable.offset = 0;
    }

    private static filterText(filter: ColumnFilterComponent, data: {}[]): {}[] {
        if (!filter.value
            && filter.filterType !== EnmFilterType.Empty
            && filter.filterType !== EnmFilterType.NotEmpty) return data;
        if (filter.filterType === EnmFilterType.Between && (!filter.value || !filter.valueMax)) return data;

        return data.filter(function (d) {
            const value: string = (ColumnFilterComponent.getValueFromObject(d, filter.prop) || '').toString().toLowerCase();
            const filterValue: string = filter.value as string;

            if (filter.filterType === EnmFilterType.Equals) return value === filterValue;
            else if (filter.filterType === EnmFilterType.NotEquals) return value !== filterValue;
            else if (filter.filterType === EnmFilterType.Contains) return value.indexOf(filterValue) !== -1;
            else if (filter.filterType === EnmFilterType.StartsWith) return value.substring(0, filterValue.length) === filter.value;
            else if (filter.filterType === EnmFilterType.EndsWith) return value.substring(value.length - filterValue.length) === filter.value;
            else if (filter.filterType === EnmFilterType.LowerThan) return value < filterValue;
            else if (filter.filterType === EnmFilterType.LowerOrEqual) return value <= filterValue;
            else if (filter.filterType === EnmFilterType.GreaterThan) return value > filterValue;
            else if (filter.filterType === EnmFilterType.GreaterOrEqual) return value >= filterValue;
            else if (filter.filterType === EnmFilterType.Empty) return !value && value !== '0';
            else if (filter.filterType === EnmFilterType.NotEmpty) return value || value === '0';
            else if (filter.filterType === EnmFilterType.Between) return filterValue <= value && value <= (filter.valueMax as string);
            else return false;
        }) as {}[];
    }
    private static filterOptions(filter: ColumnFilterComponent, data: {}[]): {}[] {
        if (filter.options.length === filter.selectedOptions.length) return data;

        const selectedValues: any[] = filter.selectedOptions.map(o => o.id);

        return data.filter(function (d) {
            return selectedValues.filter(f => f === ColumnFilterComponent.getValueFromObject(d, filter.prop)).length > 0;
        }) as {}[];
    }
    private static filterDate(filter: ColumnFilterComponent, data: {}[]): {}[] {
        if (!filter.value
            && filter.filterType !== EnmFilterType.Empty
            && filter.filterType !== EnmFilterType.NotEmpty) return data;
        if (filter.filterType === EnmFilterType.Between && (!filter.value || !filter.valueMax)) return data;

        let filterDate: Date = (filter.value as moment_.Moment).toDate();
        if (filterDate.toString() === 'Invalid Date') return data;

        filterDate = moment(filterDate).add(filterDate.getTimezoneOffset(), 'm').toDate();
        ColumnFilterComponent.setDateAccordingToFilter(filterDate, filter.type);

        let filterDateMax: Date;
        if (filter.filterType === EnmFilterType.Between && filter.valueMax) {
            filterDateMax = (filter.valueMax as moment_.Moment).utc().toDate();
            if (filterDateMax.toString() === 'Invalid Date') return data;
            ColumnFilterComponent.setDateAccordingToFilter(filterDateMax, filter.type);
        }

        return data.filter(function (d) {
            const rawValue = ColumnFilterComponent.getValueFromObject(d, filter.prop);
            if (!rawValue && filter.filterType !== EnmFilterType.Empty && filter.filterType !== EnmFilterType.NotEmpty) return false;

            const value: Date = new Date(rawValue);
            ColumnFilterComponent.setDateAccordingToFilter(value, filter.type);

            if (filter.filterType === EnmFilterType.Equals) return value.getTime() === filterDate.getTime();
            else if (filter.filterType === EnmFilterType.NotEquals) return value.getTime() !== filterDate.getTime();
            else if (filter.filterType === EnmFilterType.LowerThan) return value.getTime() < filterDate.getTime();
            else if (filter.filterType === EnmFilterType.LowerOrEqual) return value.getTime() <= filterDate.getTime();
            else if (filter.filterType === EnmFilterType.GreaterThan) return value.getTime() > filterDate.getTime();
            else if (filter.filterType === EnmFilterType.GreaterOrEqual) return value.getTime() >= filterDate.getTime();
            else if (filter.filterType === EnmFilterType.Empty) return !rawValue;
            else if (filter.filterType === EnmFilterType.NotEmpty) return rawValue;
            else if (filter.filterType === EnmFilterType.Between) return filterDate.getTime() <= value.getTime() && value.getTime() <= filterDateMax.getTime();
            else return false;
        }) as {}[];
    }

    private static setDateAccordingToFilter(date: Date, type: EnmPropertyType) {
        date.setMilliseconds(0);
        if (type === EnmPropertyType.Time || type === EnmPropertyType.TimeFull) date.setFullYear(2000, 1, 1);
        if (type !== EnmPropertyType.DateTimeFull && type !== EnmPropertyType.TimeFull) date.setSeconds(0);
        if (type === EnmPropertyType.Date) date.setHours(0, 0);
    }

    private static getValueFromObject(object: any, property: string): any {
        const fragments: string[] = property.split('.');
        for (var i: number = 0; i < fragments.length; i++) {
            object = object[fragments[i]];
            if (object === undefined || object === null) break;
        }
        return object;
    }
}
